import { useId, type ReactNode } from 'react';

import type { DeniedStateProps } from './types';
import { deniedStateStyles } from './styles';

const DEFAULT_TITLE = 'Access restricted';

/**
 * "You do not have access here" — said plainly, without saying why.
 *
 * THIS COMPONENT MUST NOT LEAK WHAT THE USER COULD HAVE HAD.
 *
 * No permission code, no list of missing capabilities, no "you would need
 * X", no record of what this role can see elsewhere. That is why the props are
 * only a title, a message and a description, and why none of them accepts a
 * `PermissionSpec` or a set of codes. Anything that renders the requirement
 * hands an operator a shopping list — and a shopping list for a platform
 * console is a map of the tenant estate, told to whoever was refused once.
 * Roles are also often guessable: "platform.admin" is not a secret.
 *
 * The rule is that a refusal describes the DESTINATION, not the CONDITION. "You
 * do not have access to this page" is safe. "You need the `billing.write`
 * permission" is not, and neither is "ask an administrator for
 * billing.write" — that confirms the code exists.
 *
 * `role="status"` is POLITE, unlike `ErrorState`. A refusal is not an urgent
 * fault; it needs no interruption beyond announcing itself on arrival.
 *
 * Whether to render this at all is the caller's decision. Hiding a control is
 * `@/shared/permissions/AppPermissionGate`; rendering this is for a whole route
 * or section that was genuinely refused.
 */
export function DeniedState({
  title = DEFAULT_TITLE,
  message = 'You do not have access to this page.',
  description,
}: DeniedStateProps): ReactNode {
  const titleId = useId();

  return (
    <div role="status" aria-labelledby={titleId} className={deniedStateStyles.root}>
      <p id={titleId} className={deniedStateStyles.title}>
        {title}
      </p>

      <p className={deniedStateStyles.message}>{message}</p>

      {description === undefined ? null : (
        <p className={deniedStateStyles.description}>{description}</p>
      )}
    </div>
  );
}
